import { useState } from 'react';

const capabilities = [
  {
    id: 'omnichannel',
    label: 'Omnichannel',
    icon: 'ri-store-2-line',
    title: 'One System. No Blind Spots.',
    subtitle: 'Every transaction across in-store, online, and mobile flows into one clear view of your revenue. No gaps. No guesswork.',
    description: 'Most businesses are running three separate dashboards and still can\'t tell you what they made yesterday. We unify every channel — in-store, online, mobile, in-app — into a single real-time view. You stop reconciling manually and start actually understanding your revenue.',
    features: [
      'Unified reporting across all channels',
      'Real-time transaction monitoring',
      'Automatic reconciliation & settlement',
      'Cross-channel customer insights',
    ],
    stat: { value: '180+', label: 'POS & Platform Integrations' },
    image: '/images/sol-omni-01.jpg',
  },
  {
    id: 'gateway',
    label: 'Gateway',
    icon: 'ri-shield-check-line',
    title: 'A Checkout That Doesn\'t Cost You Sales.',
    subtitle: 'Every second of friction at checkout is revenue you\'re leaving on the table.',
    description: 'Slow load times, clunky forms, and unnecessary redirects kill conversions before the customer even decides to leave. Our gateway is built to be fast, clean, and invisible — so the only thing standing between your customer and a completed purchase is their decision to buy.',
    features: [
      'Sub-second authorization speeds',
      'Tokenization & PCI-DSS compliance',
      '3D Secure 2.0 with adaptive routing',
      'Customizable checkout experience',
    ],
    stat: { value: '99.99%', label: 'Gateway Uptime' },
    image: '/images/sol-gateway-01.jpg',
  },
  {
    id: 'recurring',
    label: 'Recurring',
    icon: 'ri-repeat-line',
    title: 'Stop Losing Revenue to Failed Payments.',
    subtitle: 'Most subscription businesses lose 5–10% of revenue to failed charges they never recover.',
    description: 'A card decline shouldn\'t mean a lost customer. We handle the retry logic, the dunning sequences, and the billing edge cases so your recurring revenue stays predictable — and your team isn\'t chasing down payments manually every month.',
    features: [
      'Flexible billing cycles & proration',
      'Smart dunning & retry logic',
      'Automated invoicing & tax handling',
      'Customer self-service portal',
    ],
    stat: { value: '+28%', label: 'Avg. Retention Improvement' },
    image: '/images/sol-recurring-02.jpg',
  },
  {
    id: 'fraud',
    label: 'Fraud',
    icon: 'ri-shield-keyhole-line',
    title: 'Block Fraud. Not Customers.',
    subtitle: 'Fraud tools that flag legitimate transactions cost you more than the fraud itself.',
    description: 'We catch real threats without adding friction to good sales. Our adaptive scoring learns your business patterns to reduce false declines while stopping sophisticated attacks before they cost you.',
    features: [
      'Real-time risk scoring & analysis',
      'Machine learning pattern detection',
      'Chargeback representment tools',
      'Custom rule engine & thresholds',
    ],
    stat: { value: '-42%', label: 'Fraud Loss Reduction' },
    image: '/images/sol-fraud-02.jpg',
  },
  {
    id: 'pos',
    label: 'POS',
    icon: 'ri-terminal-line',
    title: 'Your In-Store Data Shouldn\'t Live in a Silo.',
    subtitle: 'What happens at the register should show up in the same place as everything else.',
    description: 'When your in-store and online data don\'t talk to each other, you\'re making decisions with half the picture. We integrate with 180+ POS systems so your physical transactions flow into the same reporting layer as your digital ones — no manual exports, no reconciliation headaches.',
    features: [
      '180+ POS system integrations',
      'Tableside & mobile ordering',
      'Real-time inventory sync',
      'Unified online + offline reporting',
    ],
    stat: { value: '3 min', label: 'Avg. Setup Time' },
    image: '/images/sol-pos-01.jpg',
  },
  {
    id: 'api',
    label: 'API',
    icon: 'ri-code-s-slash-line',
    title: 'Build on Infrastructure That Actually Tells You Things.',
    subtitle: 'Most payment APIs give you a transaction ID and call it a day.',
    description: 'We give your team the data, the hooks, and the flexibility to build payment flows that actually fit your product — not the other way around. Full documentation, sandbox environments, and a team that answers questions when something doesn\'t make sense.',
    features: [
      'RESTful APIs & webhooks',
      'SDKs for web, iOS, and Android',
      'Sandbox & staging environments',
      'Comprehensive developer documentation',
    ],
    stat: { value: '< 2 hrs', label: 'Avg. Integration Time' },
    image: '/images/sol-api-01.jpg',
  },
];

export default function CapabilitiesDetail() {
  const [active, setActive] = useState(0);
  const cap = capabilities[active];

  return (
    <section className="py-28 px-5 md:px-[75px]" style={{ background: 'linear-gradient(135deg, #1a3870 0%, #061e45 100%)' }}>
      <div className="max-w-[1400px] mx-auto">

        {/* Section Header */}
        <div className="mb-16">
          <p
            className="text-[#d3b366] text-[10px] uppercase tracking-[0.25em] font-medium mb-5"
            style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
          >
            Capabilities
          </p>
          <h2
            className="text-white text-4xl md:text-5xl lg:text-[54px] font-light leading-[1.1] max-w-2xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            The System Behind <em className="not-italic text-[#d3b366]">Every Transaction.</em>
          </h2>
        </div>

        {/* Tab Bar */}
        <div className="flex flex-wrap gap-0 border-b border-white/10 mb-16">
          {capabilities.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setActive(i)}
              className="whitespace-nowrap flex items-center gap-3 px-6 py-4 text-[11px] uppercase tracking-[0.15em] font-medium transition-all duration-200 cursor-pointer"
              style={{
                fontFamily: "'Google Sans Flex', sans-serif",
                color: i === active ? '#d3b366' : 'rgba(255,255,255,0.35)',
                borderBottom: i === active ? '2px solid #d3b366' : '2px solid transparent',
                background: 'transparent',
              }}
              onMouseEnter={(e) => {
                if (i !== active) {
                  (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.65)';
                }
              }}
              onMouseLeave={(e) => {
                if (i !== active) {
                  (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.35)';
                }
              }}
            >
              <i className={`${c.icon} text-base`} />
              {c.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div
          key={cap.id}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start"
          style={{ animation: 'tabFadeIn 400ms ease-out' }}
        >
          {/* Image */}
          <div className="w-full h-80 lg:h-[480px] overflow-hidden">
            <img
              src={cap.image}
              alt={cap.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="pt-2">
            {/* Title */}
            <h3
              className="text-white text-2xl md:text-3xl font-medium mb-3 leading-snug"
              style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
            >
              {cap.title}
            </h3>

            {/* Subtitle */}
            <p
              className="text-[#d3b366] text-[10px] uppercase tracking-[0.25em] font-medium mb-6"
              style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
            >
              {cap.subtitle}
            </p>

            {/* Description */}
            <p
              className="text-white/55 text-[15px] leading-relaxed mb-8"
              style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
            >
              {cap.description}
            </p>

            {/* Features */}
            <ul className="flex flex-col gap-3 mb-8">
              {cap.features.map((feature) => (
                <li key={feature} className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d3b366] text-sm mt-0.5 shrink-0" />
                  <span
                    className="text-white/65 text-[15px]"
                    style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                  >
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            {/* Stat */}
            <div className="border-t border-white/10 pt-6">
              <p
                className="text-[#d3b366] text-3xl font-light mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {cap.stat.value}
              </p>
              <p
                className="text-white/35 text-[10px] uppercase tracking-[0.15em]"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                {cap.stat.label}
              </p>
            </div>
          </div>
        </div>

      </div>

      <style>{`
        @keyframes tabFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
